import { lazy, type ComponentType } from 'react';

const RELOAD_KEY = 'chunk-reload-attempted';

function isStaleChunkError(error: unknown): boolean {
  const message = (error as Error | undefined)?.message ?? '';
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module')
  );
}

/**
 * `React.lazy` wrapper that recovers from the stale-chunk error that follows
 * a deploy. Vite content-hashes every chunk (e.g. `Dashboard-c4831dd3.js`)
 * and replaces them on each release — a tab opened before the deploy still
 * references the old filenames, so the next dynamic import 404s.
 *
 * On that specific failure, reload once so the browser fetches the fresh
 * `index.html` and its new chunk hashes. A `sessionStorage` guard caps the
 * recovery at one reload per session, so a genuinely broken chunk (CDN
 * outage, bad build) still surfaces to the ErrorBoundary on the second try.
 *
 * The guard is cleared only on a *successful* import — never on app boot —
 * so a broken build can't trigger an endless reload loop, while a future
 * stale-chunk scenario in the same session can still recover once the app
 * has proved it can load.
 */
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const module = await factory();
      sessionStorage.removeItem(RELOAD_KEY);
      return module;
    } catch (err) {
      if (isStaleChunkError(err) && !sessionStorage.getItem(RELOAD_KEY)) {
        sessionStorage.setItem(RELOAD_KEY, '1');
        globalThis.location.reload();
        // Keep the import promise pending so React's Suspense doesn't try to
        // render before the reload completes.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
